import React, { useState, useRef, useEffect } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { withRouter } from 'react-router-dom';
import { preCheckoutConfirm } from '../../../actions/creators/checkout';
import { parseSessionIsoDateForDisplay } from '../../../utils/dates';
import { getBootstrapFilters, getCinema, isShowtimeConfirmationPopupEnabled } from '../../../reducers';
import Button from '../../Button';
import moment from 'moment';
import { CinemaType } from '../../../types/entities';
import ShowtimeTooltip from './ShowtimeTooltip';
import FitVersionLabel from './FitVersionLabel';
import buildVersionItems from './buildVersionItems';

const StyledButton = styled(Button)<{ availability: string, disabled: boolean }>`
  width: 100%;

  ${props => {
    if (props.disabled) {
      return '';
    }

    switch(props.availability) {
      case 'low': return `var(--color-status-danger)`;
      case 'mid': return `var(--color-status-mid)`;
      default: return `--local-border-color: var(--color-status-safe)`;
    }
  }};

  ${props => props.disabled && '&, &:hover { --local-text-color: #555 }'}
`;

interface Props {
  attributes?: { [key: string]: any },
  session: any,
  movieName: string,
  version: string | { [key: string]: any },
  cinema: string | CinemaType,
  date: moment.Moment,
  disabled?: boolean,
  askConfirmation?: boolean,
  history: any,
  path: string,
  preCheckoutConfirm: (payload: any) => Promise<any>,
}

function ShowtimeButton(props: Props) {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  const [referenceEl, setReferenceEl] = useState<HTMLDivElement | null>(null);
  const showTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const onMouseUp = () => {
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
      setTooltipVisible(false);
    };
    document.addEventListener('mouseup', onMouseUp);
    return () => {
      document.removeEventListener('mouseup', onMouseUp);
      if (showTimerRef.current) clearTimeout(showTimerRef.current);
    };
  }, []);

  const getVersionLabel = () =>
    !!props.version && (typeof props.version === 'string' ? props.version : props.version.label);

  const getCinemaName = () => {
    if (!props.cinema) return '';
    return typeof props.cinema === 'string' ? props.cinema : props.cinema.name;
  };

  const onMouseEnter = () => {
    showTimerRef.current = setTimeout(() => setTooltipVisible(true), 600);
  };

  const onMouseLeave = () => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    setTooltipVisible(false);
  };

  const onTouchStart = (e: React.TouchEvent) => {
    e.preventDefault();
    showTimerRef.current = setTimeout(() => setTooltipVisible(true), 600);
  };

  const onTouchEnd = () => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
    setTooltipVisible(false);
  };

  const onTouchMove = () => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
  };

  const onButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    const { path, history } = props;
    if (props.askConfirmation !== false) {
      props.preCheckoutConfirm({
        movie: props.movieName,
        version: getVersionLabel(),
        date: props.date.format('dddd D [de] MMMM'),
        time: props.date.format('hh:mm A'),
        cinema: getCinemaName(),
      }).then(() => { history.push(path); }).catch();
    } else {
      history.push(path);
    }
  };

  function onShowTooltipClick(e: React.MouseEvent<HTMLSpanElement>) {
    debugger;
    e.preventDefault();
    e.stopPropagation();
    setTooltipVisible(true);
  }

  const { session, date, path, cinema, disabled: propsDisabled } = props;
  const disabled = propsDisabled || (!!cinema && cinema instanceof Object && cinema.status === 'maintenance');
  
  return (
    <div
      ref={setReferenceEl}
      style={{
        position: 'relative',
        display: 'inline-block',
        WebkitUserSelect: 'none',
        userSelect: 'none',
        minWidth: '100px',
      } as React.CSSProperties}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
      onTouchMove={onTouchMove}
    >
      {props.version && tooltipVisible && (
        <ShowtimeTooltip
          visible={tooltipVisible}
          version={props.version}
          attributes={props.attributes}
          referenceEl={referenceEl}
        />
      )}
      <StyledButton
        href={disabled ? '#!' : path}
        availability={disabled ? null : session.availability}
        outline
        small
        onClick={disabled ? null : onButtonClick}
        disabled={disabled}
      >
        {date.format('hh:mm A')}
        {props.attributes && <FitVersionLabel items={buildVersionItems(props.version, props.attributes)} onShowTooltipClick={onShowTooltipClick} />}
      </StyledButton>
    </div>
  );
}

const mapStateToProps = (state: any, props: any) => {
  const cinema = props.cinema || getCinema(state, ('cinema' in props.session) ? props.session.cinema.id : props.session.cinema_id);
  return {
    askConfirmation: isShowtimeConfirmationPopupEnabled(state) && props.askConfirmation,
    attributes: getBootstrapFilters(state),
    cinema: cinema,
    date: parseSessionIsoDateForDisplay(props.session.datetime),
    path: '/checkout/' + props.session.id,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  preCheckoutConfirm: (payload: any) => dispatch(preCheckoutConfirm(payload)),
});

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(ShowtimeButton));